// Single authoritative source for the render pass's MSAA sample count.
//
// Every GPURenderPipeline that participates in RenderService's main render
// pass (static/skinned/outline/papercraft/lines/gizmos/shadows/diorama/
// reticle/plane/sphere/collision - see render_service.ts's single render
// pass and every *_renderpipeline.ts under this directory) must use this
// constant's value in its `multisample.count`, and RenderService must use
// it for both the multisampled color texture it resolves into the canvas
// and the depth texture, so every attachment bound in the same render pass
// agrees on sample count - WebGPU requires this exactly, not approximately
// (mismatched sample counts between pipeline and pass attachments is a
// validation error that would surface as a black canvas/dropped frame in
// production rather than a clear message in most browsers).
//
// Compute pipelines (animation_compute_pipeline.ts, etc.) never touch this -
// GPUComputePipeline has no multisample state, and compute passes have no
// color/depth attachments at all.
//
// RENDER_SAMPLE_COUNT === 1 must remain a fully valid, supported value if
// this is ever changed back (e.g. for a low-end/compatibility fallback):
// WebGPU only allows sample counts of 1 or 4 for
// render-attachment/multisample textures (the "1 or 4" rule from the spec),
// so those are the only two values this constant should ever hold. See
// resolveTarget/attachment wiring in render_service.ts, which branches on
// this value: count 4 renders into an offscreen multisampled color target
// and resolves into the canvas's current texture; count 1 renders directly
// into the canvas's current texture with no resolve target, matching the
// pre-MSAA behavior exactly.
export const RENDER_SAMPLE_COUNT: 1 | 4 = 4;

/** True when MSAA is active for the main render pass (count > 1). */
export function is_multisampled(sample_count: number = RENDER_SAMPLE_COUNT): boolean {
  return sample_count > 1;
}
